import React from "react";
import { X, ImageOff, Check } from "lucide-react"; // Or your preferred icon library
import SatoriButton from "./satori-ui/button"; // Update import as per your setup
import { IImageAsset } from "./draggable-asset";

// Type definitions (adapt as needed)
interface ImageItem {
  id: string;
  url: string;
  alt?: string;
}

interface ImageLibraryDialogProps {
  open: boolean;
  images: IImageAsset[];
  selectedUrl?: string;
  onClose: () => void;
  onSelect: (img: IImageAsset) => void;
  onUpload?: () => void;
}

/**
 * ImageLibraryDialog – Modal Dialog for Browsing and Selecting Images
 *
 * @component
 * @description
 * Displays a modal overlay for viewing, selecting, and (optionally) uploading images
 * from the global Image Library. Supports both selection (with highlight) and upload action.
 *
 * @props
 * @param {boolean} open – Whether the dialog is visible.
 * @param {IImageAsset[]} images – Array of available image assets to display.
 * @param {string} [selectedUrl] – Optional. The URL of the currently selected image (for highlight).
 * @param {() => void} onClose – Callback to close the dialog.
 * @param {(img: IImageAsset) => void} onSelect – Callback fired when an image is selected.
 * @param {() => void} [onUpload] – Optional. Callback to trigger the image upload action.
 *
 * @features
 * - Modal overlay with fade-in animation and backdrop blur
 * - Displays all images in a responsive grid with hover/active effects
 * - Highlights the currently selected image (if any)
 * - Shows placeholder/empty state when no images are available
 * - Close button and optional upload button
 *
 * @remarks
 * - Intended for internal use as the main image selection modal in PageBuilder
 * - Not intended for general use outside the builder UI
 *
 * @example
 * <ImageLibraryDialog
 *   open={isOpen}
 *   images={imageAssets}
 *   selectedUrl={selectedImageUrl}
 *   onClose={() => setIsOpen(false)}
 *   onSelect={handleSelectImage}
 *   onUpload={handleUploadImage}
 * />
 *
 * @since 2025-06-17
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default function ImageLibraryDialog ({
  open,
  images,
  selectedUrl,
  onClose,
  onSelect,
  onUpload,
} : ImageLibraryDialogProps) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
      <div className="relative bg-[#f8fafc] border border-gray-200 shadow-2xl rounded-2xl max-w-[650px] w-[96vw] p-8 overflow-hidden animate-fadeIn">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 bg-white rounded-full p-1.5 shadow hover:bg-blue-50 transition"
          onClick={onClose}
          aria-label="Close"
        >
          <X size={20} className="text-gray-400" />
        </button>

        {/* Title and Actions */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Select an Image</h2>
          {onUpload && (
            <SatoriButton
              variant="primary"
              size="md"
              onClick={onUpload}
            >
              Upload Image
            </SatoriButton>
          )}
        </div>

        {/* Image Grid */}
        {images.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12">
            <ImageOff size={48} className="text-gray-300 mb-3" />
            <p className="text-gray-500 text-lg mb-1">No images found.</p>
            <p className="text-gray-400 text-sm">Upload an image to get started.</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-5 max-h-[370px] overflow-y-auto pb-3 pr-1">
            {images.map(img => (
              <button
                key={img.id}
                onClick={() => onSelect(img)}
                className={`
                  group relative w-full aspect-[4/3] rounded-xl overflow-hidden border
                  ${selectedUrl === img.url ? "border-blue-400 ring-2 ring-blue-200" : "border-gray-200"}
                  shadow hover:shadow-lg transition-all
                  focus:outline-none
                `}
              >
                <img
                  src={img.url}
                  alt={img.alt || "Gallery image"}
                  className="object-cover w-full h-full transition-transform group-hover:scale-105"
                />
                {selectedUrl === img.url && (
                  <div className="absolute inset-0 bg-blue-400/10 flex items-center justify-center rounded-xl">
                    <Check size={32} className="text-blue-400" />
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
